import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { useNetwork } from '@/contexts/NetworkContext'
import { AdUnitsApi, type AdUnit } from '@/services/adUnits/adUnitsApi'
import type { Targeting } from '@/services/targeting/targeting'
import {
  AdRequestDiagnostics,
  EMPTY_DIAGNOSTIC_FORM,
  diagnosticTargetingCount,
  diagnosticTargetingFrom,
  type DiagnosticFormValues,
} from './AdRequestDiagnostics'
import { DiagnosticAdUnitPicker } from './DiagnosticAdUnitPicker'

/** More targeted ad units than this are not picked up front: the run would take too long. */
const MAX_TARGETED_AD_UNITS = 10

export interface LineItemAdRequestDiagnosticsProps {
  lineItemId: string
  /** The targeting as edited, which the request starts out matching. */
  targeting: Targeting
}

/**
 * Tests an ad request for one line item on ad units of your choosing, to see
 * whether it would deliver there and why not. The request starts out carrying
 * what the line item targets, and on the ad units it targets by name.
 */
export function LineItemAdRequestDiagnostics({
  lineItemId,
  targeting,
}: LineItemAdRequestDiagnosticsProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const [initial] = useState<DiagnosticFormValues>(() => ({
    ...EMPTY_DIAGNOSTIC_FORM,
    requestType: 'NORMAL',
    ...diagnosticTargetingFrom(targeting),
  }))
  const [values, setValues] = useState(initial)
  const prefilled = diagnosticTargetingCount(initial) > 0

  const targetedIds = targeting.adUnitTarget?.negated
    ? []
    : (targeting.adUnitTarget?.adUnits ?? []).map((adUnit) => adUnit.id)
  const prefillIds = targetedIds.length <= MAX_TARGETED_AD_UNITS ? targetedIds : []
  const { data: targetedAdUnits = [], isLoading: targetedLoading } = useQuery({
    queryKey: ['diagnostics-targeted-adunits', scopeKey, prefillIds.join(',')],
    enabled: prefillIds.length > 0,
    queryFn: () => AdUnitsApi.getByIds(prefillIds),
    staleTime: 5 * 60 * 1000,
  })
  // Until one is picked or removed, the ad units are the ones the line item targets.
  const [picked, setPicked] = useState<AdUnit[]>()
  const adUnits = picked ?? targetedAdUnits

  return (
    <AdRequestDiagnostics
      values={values}
      onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
      adUnits={adUnits}
      adUnitsLoading={!picked && targetedLoading}
      lineItemId={lineItemId}
      targetingHint={prefilled ? t('diagnostics.targetingFromLineItem') : undefined}
      adUnitPicker={<DiagnosticAdUnitPicker value={adUnits} onChange={setPicked} />}
    />
  )
}
